import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import Badge from './Badge';
import DocumentWallet from './DocumentWallet';
import AuditLog from './AuditLog';
import EntityLink from './EntityLink';
import NoteText, { InvoiceNotes } from './NoteText';
import { useAuth } from '../context/AuthContext';

export default function APInvoicePanel({ invoiceId, onClose, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [showPayForm, setShowPayForm] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const EMPTY_PAY_FORM = { payment_date: '', cash_amount: '', epd_rate: '', epd_amount: '', bgc_amount: '', advance_deduction_amount: '', trace_id: '', notes: '' };
  const [payForm, setPayForm] = useState(EMPTY_PAY_FORM);
  const [saving, setSaving] = useState(false);
  const [payNotice, setPayNotice] = useState('');

  function load() {
    api.get(`/ap-invoices/${invoiceId}`).then(r => setData(r.data)).catch(() => setData(null));
  }

  useEffect(() => { load(); }, [invoiceId]);

  async function savePayment() {
    setSaving(true);
    try {
      const payload = {
        ...payForm,
        cash_amount: Number(payForm.cash_amount || 0),
        epd_rate: payForm.epd_rate ? Number(payForm.epd_rate) : null,
        epd_amount: payForm.epd_amount ? Number(payForm.epd_amount) : 0,
        bgc_amount: payForm.bgc_amount ? Number(payForm.bgc_amount) : 0,
        advance_deduction_amount: payForm.advance_deduction_amount ? Number(payForm.advance_deduction_amount) : 0,
      };
      if (editingPaymentId) {
        await api.put(`/ap-invoices/${invoiceId}/payments/${editingPaymentId}`, payload);
        setPayNotice('');
      } else {
        const r = await api.post(`/ap-invoices/${invoiceId}/payments`, payload);
        setPayNotice(r.data?.pay_warning ? `Payment recorded although this invoice is not Payable now: ${r.data.pay_warning}. Logged in the audit trail.` : '');
      }
      setPayForm(EMPTY_PAY_FORM);
      setEditingPaymentId(null);
      setShowPayForm(false);
      load();
      if (onChanged) onChanged();
    } finally { setSaving(false); }
  }

  function startEditPayment(p) {
    setPayForm({
      payment_date: p.payment_date || '',
      cash_amount: p.cash_amount ?? '',
      epd_rate: p.epd_rate ?? '',
      epd_amount: p.epd_amount ?? '',
      bgc_amount: p.bgc_amount ?? '',
      advance_deduction_amount: p.advance_deduction_amount ?? '',
      trace_id: p.trace_id || '',
      notes: p.notes || '',
    });
    setEditingPaymentId(p.id);
    setShowPayForm(true);
  }

  async function setVerification(paymentId, status) {
    await api.put(`/ap-invoices/${invoiceId}/payments/${paymentId}/verify`, { verification_status: status });
    load();
    if (onChanged) onChanged();
  }

  function startEdit() {
    setEditForm({ ...data });
    setIsEditing(true);
    setEditError('');
  }

  async function saveEdit() {
    if (!editForm.invoice_number?.trim()) { setEditError('Invoice number required'); return; }
    setEditSaving(true);
    setEditError('');
    try {
      await api.put(`/ap-invoices/${invoiceId}`, editForm);
      setIsEditing(false);
      setEditForm(null);
      load();
      if (onChanged) onChanged();
    } catch (e) {
      setEditError('Save failed: ' + (e.response?.data?.error || e.message));
    } finally { setEditSaving(false); }
  }

  function cancelEdit() {
    setIsEditing(false);
    setEditForm(null);
    setEditError('');
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="quick-panel" style={{ width: 500 }}>
        <div className="drawer-header">
          <h3 className="drawer-title">{isEditing ? 'Edit Invoice' : (data?.invoice_number || 'Loading…')}</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isEditing && user?.role === 'admin' && (
              <button className="btn btn-sm btn-secondary" onClick={startEdit}>✏️ Edit</button>
            )}
            <button className="btn btn-sm btn-secondary" onClick={() => navigate(`/ap-invoices/${invoiceId}`)}>
              View Full Page ↗
            </button>
            <button className="drawer-close" onClick={onClose}>×</button>
          </div>
        </div>

        {data && !isEditing && (
          <div className="panel-body">
            <div className="panel-summary-line">
              <Badge>{data.status}</Badge>
              <Badge variant={data.invoice_type === 'passthrough' ? 'info' : undefined}>
                {data.invoice_type === 'passthrough' ? 'Allowance / Reimbursement' : 'Standard'}
              </Badge>
              <EntityLink type="vendor" id={data.vendor_id}>{data.vendor_name}</EntityLink>
              {' · '}
              <EntityLink type="contractor" id={data.contractor_id}>{data.contractor_name}</EntityLink>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13, marginBottom: 16 }}>
              <DetailRow label="Invoice #" value={data.invoice_number} />
              <DetailRow label="Vendor" value={<EntityLink type="vendor" id={data.vendor_id}>{data.vendor_name}</EntityLink>} />
              <DetailRow label="Contractor" value={<EntityLink type="contractor" id={data.contractor_id}>{data.contractor_name}</EntityLink>} />
              <DetailRow label="Invoice Date" value={data.invoice_date} />
              <DetailRow label="Period" value={data.period_start && data.period_end ? `${data.period_start} – ${data.period_end}` : null} />
              <DetailRow label="Hours Billed" value={data.hours_billed} />
              <DetailRow label="Hours Approved" value={data.hours_approved} />
              <DetailRow label="Rate" value={data.rate != null ? `$${data.rate}/hr` : null} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 20 }}>
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Amount to Pay</div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>${data.amount_to_pay.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              </div>
              <div style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 12 }}>
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>Outstanding</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: data.summary.outstanding > 0 ? 'var(--warning)' : 'var(--success)' }}>
                  ${data.summary.outstanding.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            {data.advance && (
              <div className="form-hint" style={{ marginBottom: 12 }}>
                Contractor advance balance: <strong>${data.advance.balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                {' '}(advanced ${data.advance.total_advanced.toLocaleString(undefined, { minimumFractionDigits: 2 })}, recovered ${data.advance.total_recovered.toLocaleString(undefined, { minimumFractionDigits: 2 })}) – see the contractor's Advances tab.
              </div>
            )}

            <InvoiceNotes notes={data.notes} queueNote={data.queue_note} />

            <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Payments</h4>
            {data.payments.length === 0 && <div className="form-hint" style={{ marginBottom: 12 }}>No payments recorded yet.</div>}
            {data.payments.map(p => (
              <div key={p.id} style={{ background: 'var(--surface)', borderRadius: 'var(--radius)', padding: 10, marginBottom: 8, fontSize: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <strong>${p.cash_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
                  <Badge variant={p.verification_status === 'verified' ? 'success' : 'warning'}>{p.verification_status}</Badge>
                </div>
                <div style={{ color: 'var(--muted)', fontSize: 11 }}>
                  {p.payment_date} {p.trace_id && `· Trace: ${p.trace_id}`}
                  {p.epd_amount ? ` · EPD: $${p.epd_amount}` : ''}
                  {p.bgc_amount ? ` · BGC: $${p.bgc_amount}` : ''}
                  {p.advance_deduction_amount ? ` · Advance: $${p.advance_deduction_amount}` : ''}
                </div>
                <NoteText text={p.notes} />
                {user?.role === 'admin' && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVerification(p.id, 'verified')}>✓ Verify</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setVerification(p.id, 'disputed')}>✗ Dispute</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => startEditPayment(p)}>✎ Edit</button>
                  </div>
                )}
              </div>
            ))}

            {payNotice && <PayWarning text={payNotice} />}

            <button className="btn btn-secondary" style={{ width: '100%', marginTop: 14 }} onClick={() => { setShowPayForm(true); setPayNotice(''); }}>
              + Record Payment
            </button>

            {showPayForm && (
              <div style={{ marginTop: 16, padding: 12, background: 'var(--surface)', borderRadius: 'var(--radius)' }}>
                <h5 style={{ marginBottom: 10, fontSize: 12, fontWeight: 700 }}>{editingPaymentId ? 'Edit Payment' : 'Record Payment'}</h5>
                {!editingPaymentId && data.pay_warning && (
                  <PayWarning text={`Not Payable now: ${data.pay_warning}. You can still save; the payment will be logged as entered while this warning showed.`} />
                )}
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Payment Date</label>
                  <input type="date" value={payForm.payment_date} onChange={e => setPayForm(f => ({ ...f, payment_date: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Cash Amount</label>
                  <input type="number" step="0.01" value={payForm.cash_amount} onChange={e => setPayForm(f => ({ ...f, cash_amount: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                  <div className="form-field">
                    <label>EPD Rate (%)</label>
                    <input type="number" step="0.01" value={payForm.epd_rate} onChange={e => setPayForm(f => ({ ...f, epd_rate: e.target.value }))} />
                  </div>
                  <div className="form-field">
                    <label>EPD Amount</label>
                    <input type="number" step="0.01" value={payForm.epd_amount} onChange={e => setPayForm(f => ({ ...f, epd_amount: e.target.value }))} />
                  </div>
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>BGC Amount</label>
                  <input type="number" step="0.01" value={payForm.bgc_amount} onChange={e => setPayForm(f => ({ ...f, bgc_amount: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Advance Deduction</label>
                  <input type="number" step="0.01" value={payForm.advance_deduction_amount} onChange={e => setPayForm(f => ({ ...f, advance_deduction_amount: e.target.value }))} />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Bank Trace ID</label>
                  <input value={payForm.trace_id} onChange={e => setPayForm(f => ({ ...f, trace_id: e.target.value }))} placeholder="e.g. 202607006967538" />
                </div>
                <div className="form-field" style={{ marginBottom: 10 }}>
                  <label>Notes</label>
                  <input value={payForm.notes || ''} onChange={e => setPayForm(f => ({ ...f, notes: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setShowPayForm(false); setEditingPaymentId(null); setPayForm(EMPTY_PAY_FORM); }}>Cancel</button>
                  <button className="btn btn-primary" style={{ flex: 1 }} onClick={savePayment} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
              </div>
            )}

            <DocumentWallet entityType="ap_invoice" entityId={invoiceId} entityName={data?.invoice_number} />
            <AuditLog tableName="ap_invoices" recordId={invoiceId} />
          </div>
        )}

        {editForm && isEditing && (
          <div className="panel-body">
            {editError && <div style={{ background: '#fee', color: '#c33', padding: 10, borderRadius: 4, marginBottom: 12, fontSize: 12 }}>{editError}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
              <div className="form-field"><label>Invoice #</label><input type="text" value={editForm.invoice_number || ''} onChange={e => setEditForm(f => ({ ...f, invoice_number: e.target.value }))} /></div>
              <div className="form-field"><label>Invoice Date</label><input type="date" value={editForm.invoice_date || ''} onChange={e => setEditForm(f => ({ ...f, invoice_date: e.target.value }))} /></div>
              <div className="form-field"><label>Period Start</label><input type="date" value={editForm.period_start || ''} onChange={e => setEditForm(f => ({ ...f, period_start: e.target.value }))} /></div>
              <div className="form-field"><label>Period End</label><input type="date" value={editForm.period_end || ''} onChange={e => setEditForm(f => ({ ...f, period_end: e.target.value }))} /></div>
              <div className="form-field"><label>Hours Billed</label><input type="number" step="0.01" value={editForm.hours_billed || ''} onChange={e => setEditForm(f => ({ ...f, hours_billed: e.target.value }))} /></div>
              <div className="form-field"><label>Hours Approved</label><input type="number" step="0.01" value={editForm.hours_approved || ''} onChange={e => setEditForm(f => ({ ...f, hours_approved: e.target.value }))} /></div>
              <div className="form-field"><label>Rate ($/hr)</label><input type="number" step="0.01" value={editForm.rate || ''} onChange={e => setEditForm(f => ({ ...f, rate: e.target.value }))} /></div>
              <div className="form-field"><label>Amount to Pay</label><input type="number" step="0.01" value={editForm.amount_to_pay || ''} onChange={e => setEditForm(f => ({ ...f, amount_to_pay: e.target.value }))} /></div>
              <div className="form-field">
                <label>Status</label>
                <select value={editForm.status || 'pending'} onChange={e => setEditForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="pending">Pending</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                  <option value="void">Void</option>
                </select>
              </div>
              <div className="form-field"><label>Notes</label><textarea value={editForm.notes || ''} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} rows="3" /></div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button className="btn btn-secondary" onClick={cancelEdit} disabled={editSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={saveEdit} disabled={editSaving}>{editSaving ? 'Saving…' : 'Save Changes'}</button>
            </div>
          </div>
        )}

        {!data && <div className="panel-body"><div style={{ textAlign: 'center', padding: 20, color: 'var(--muted)' }}>Loading…</div></div>}
      </div>
    </>
  );
}

// Warning only - never blocks the save (Mukesh, 27-Oct-26).
function PayWarning({ text }) {
  return (
    <div role="alert" style={{ background: '#fff4e5', color: '#8a4b00', border: '1px solid #f0b46a', padding: 10, borderRadius: 4, marginBottom: 10, marginTop: 10, fontSize: 12 }}>
      ⚠ {text}
    </div>
  );
}

function DetailRow({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <span style={{ color: 'var(--muted)', flexShrink: 0 }}>{label}</span>
      <span style={{ fontWeight: 600, textAlign: 'right' }}>{value}</span>
    </div>
  );
}
